import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Avatar,
  CircularProgress,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";
import {
  Business as BusinessIcon,
  CloudUpload as CloudUploadIcon,
  Save as SaveIcon,
  AccountBalance as BankIcon,
  ReceiptLong as TaxIcon,
  Edit as EditIcon,
  Close as CloseIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Language as WebIcon,
  LocationOn as LocationIcon,
} from "@mui/icons-material";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  getCompanyProfile,
  updateCompanyProfile,
} from "../../../Services/Services";

// Initial Form State
const initialCompanyData = {
  companyName: "Yashvitech IT Solution",
  tagline: "Empowering Business with Smart Software",
  email: "info.yashvitech@gmail.com",
  phone: "+91 9876543210",
  website: "https://www.yashvitech.com",
  address: "Raipur, Chhattisgarh, India",
  gstin: "",
  pan: "",
  bankName: "",
  accountNumber: "",
  ifscCode: "",
  branchName: "",
  logoUrl: "",
};

const CompanyProfileManagement = () => {
  const [formData, setFormData] = useState(initialCompanyData);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);

  // Modal State
  const [openEditModal, setOpenEditModal] = useState(false);

  const validateForm = () => {
    let tempErrors = {};

    // Email Validation
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      tempErrors.email = "Invalid email format (e.g. name@domain.com)";
    }

if (formData.phone) {
  if (formData.phone.length < 10) {
    tempErrors.phone = "Phone number must be exactly 10 digits";
  } else if (!/^[6-9]\d{9}$/.test(formData.phone)) {
    tempErrors.phone = "Enter a valid 10-digit mobile number (starts with 6-9)";
  }
}

    // PAN Validation
    if (formData.pan && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/i.test(formData.pan)) {
      tempErrors.pan = "Invalid PAN (Format: ABCDE1234F)";
    }

    // GSTIN Validation
    if (
      formData.gstin &&
      !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/i.test(
        formData.gstin,
      )
    ) {
      tempErrors.gstin = "Invalid GSTIN (Format: 22ABCDE1234F1Z5)";
    }

    // IFSC Validation
    if (
      formData.ifscCode &&
      !/^[A-Z]{4}0[A-Z0-9]{6}$/i.test(formData.ifscCode)
    ) {
      tempErrors.ifscCode = "Invalid IFSC (Format: SBIN0001234)";
    }

    setErrors(tempErrors);

    const errorMessages = Object.values(tempErrors);
    return {
      isValid: errorMessages.length === 0,
      errorMessages,
    };
  };

  // Fetch current settings on mount
  useEffect(() => {
    fetchCompanySettings();
  }, []);

  const fetchCompanySettings = async () => {
    setFetching(true);
    try {
      const data = await getCompanyProfile();

      // Handle array or single object response safely
      const profileData = Array.isArray(data) ? data[0] : data;

      if (profileData) {
        setFormData({
          companyName: profileData.company_name || "",
          tagline: profileData.tagline || "",
          email: profileData.email || "",
          phone: profileData.phone || "",
          website: profileData.website || "",
          address: profileData.address || "",
          gstin: profileData.gstin || "",
          pan: profileData.pan || "",
          bankName: profileData.bank_name || "",
          accountNumber: profileData.account_number || "",
          ifscCode: profileData.ifsc_code || "",
          branchName: profileData.branch_name || "",
          logoUrl: profileData.logo_url || "",
        });

        if (profileData.logo_url) {
          setLogoPreview(profileData.logo_url);
        }
      }
    } catch (error) {
      toast.error("Failed to load company profile settings");
    } finally {
      setFetching(false);
    }
  };

  // Text inputs handler
const handleInputChange = (e) => {
  const { name, value } = e.target;

  // 1. UpperCase fields (PAN, GSTIN, IFSC)
  const upperCaseFields = ["pan", "gstin", "ifscCode"];

  let finalValue = value;

  // 2. Strict Phone Handler: Only digits & Max 10 digits
  if (name === "phone") {
    finalValue = value.replace(/\D/g, "").slice(0, 10);
  } else if (upperCaseFields.includes(name)) {
    finalValue = value.toUpperCase();
  }

  setFormData((prev) => ({ ...prev, [name]: finalValue }));

  // Clear field error
  if (errors[name]) {
    setErrors((prev) => ({ ...prev, [name]: "" }));
  }
};
  // Main Logo Upload Handler
  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error("Logo file size should be less than 2MB");
        return;
      }
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file));
    }
  };

  // Save Settings
  const handleSaveProfile = async (e) => {
    e.preventDefault();

    // Check validation
    const { isValid, errorMessages } = validateForm();

    if (!isValid) {
      // ✅ Toast me exact errors list show karega
      toast.error(`Please fix errors: ${errorMessages.join(" | ")}`, {
        autoClose: 5000,
      });
      return;
    }

    setLoading(true);

    try {
      const dataPayload = new FormData();

      dataPayload.append("company_name", formData.companyName);
      dataPayload.append("tagline", formData.tagline);
      dataPayload.append("email", formData.email);
      dataPayload.append("phone", formData.phone);
      dataPayload.append("website", formData.website);
      dataPayload.append("address", formData.address);
      dataPayload.append("gstin", formData.gstin);
      dataPayload.append("pan", formData.pan);
      dataPayload.append("bank_name", formData.bankName);
      dataPayload.append("account_number", formData.accountNumber);
      dataPayload.append("ifsc_code", formData.ifscCode);
      dataPayload.append("branch_name", formData.branchName);

      if (logoFile) {
        dataPayload.append("logo", logoFile);
      }

      const response = await updateCompanyProfile(dataPayload);

      toast.success(
        response?.message || "Company profile updated successfully!",
      );

      const responseData = response?.data || response;
      if (responseData && responseData.logo_url) {
        setFormData((prev) => ({ ...prev, logoUrl: responseData.logo_url }));
        setLogoPreview(responseData.logo_url);
      }

      setOpenEditModal(false);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "300px",
        }}
      >
        <CircularProgress size={40} sx={{ color: "#2563eb" }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: { xs: 1.5, md: 2 },
        maxWidth: "1600px",
        margin: "0 auto",
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      {/* Top Header & Action */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Box>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 800,
              color: "#0f172a",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <BusinessIcon sx={{ fontSize: 28, color: "#2563eb" }} />
            Company Profile
          </Typography>
        </Box>

        <Button
          variant="contained"
          startIcon={<EditIcon />}
          onClick={() => setOpenEditModal(true)}
          sx={{
            bgcolor: "#2563eb",
            color: "#fff",
            borderRadius: "8px",
            textTransform: "none",
            fontWeight: 700,
            px: 2.5,
            py: 0.9,
            boxShadow: "0 4px 12px rgba(37, 99, 235, 0.25)",
            "&:hover": {
              bgcolor: "#1d4ed8",
            },
          }}
        >
          Edit Profile
        </Button>
      </Box>

      {/* Main Card View Display Grid */}
      <Grid container spacing={2}>
        {/* Left Column: Details Cards */}
        <Grid item xs={12} md={8} lg={8.5}>
          <Grid container spacing={2}>
            {/* 1. Basic Company Information Card */}
            <Grid item xs={12}>
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: "12px",
                  border: "1px solid #e2e8f0",
                  bgcolor: "#fff",
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 700,
                    mb: 2,
                    color: "#0f172a",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <BusinessIcon sx={{ color: "#2563eb", fontSize: 20 }} />
                  Company Overview
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="caption"
                      sx={{ color: "#64748b", fontWeight: 600 }}
                    >
                      Company Name
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: 700, color: "#0f172a", mt: 0.2 }}
                    >
                      {formData.companyName || "N/A"}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="caption"
                      sx={{ color: "#64748b", fontWeight: 600 }}
                    >
                      Tagline / Motto
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: 600, color: "#334155", mt: 0.2 }}
                    >
                      {formData.tagline || "N/A"}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#64748b",
                        fontWeight: 600,
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      <EmailIcon sx={{ fontSize: 14 }} /> Official Email
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, color: "#0f172a", mt: 0.2 }}
                    >
                      {formData.email || "N/A"}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#64748b",
                        fontWeight: 600,
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      <PhoneIcon sx={{ fontSize: 14 }} /> Contact Phone
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, color: "#0f172a", mt: 0.2 }}
                    >
                      {formData.phone || "N/A"}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#64748b",
                        fontWeight: 600,
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      <WebIcon sx={{ fontSize: 14 }} /> Website URL
                    </Typography>
                    <Typography
                      variant="body2"
                      component="a"
                      href={formData.website}
                      target="_blank"
                      rel="noreferrer"
                      sx={{
                        fontWeight: 600,
                        color: "#2563eb",
                        mt: 0.2,
                        display: "block",
                        textDecoration: "none",
                      }}
                    >
                      {formData.website || "N/A"}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#64748b",
                        fontWeight: 600,
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      <LocationIcon sx={{ fontSize: 14 }} /> Business Address
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, color: "#0f172a", mt: 0.2 }}
                    >
                      {formData.address || "N/A"}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* 2. Tax & Registration Card (Simple Clean View) */}
            <Grid item xs={12} sm={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: "12px",
                  border: "1px solid #e2e8f0",
                  bgcolor: "#fff",
                  height: "100%",
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 700,
                    mb: 2,
                    color: "#0f172a",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <TaxIcon sx={{ color: "#2563eb", fontSize: 20 }} />
                  Tax & Legal Info
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="caption"
                      sx={{ color: "#64748b", fontWeight: 600 }}
                    >
                      GSTIN Number
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 700, color: "#0f172a", mt: 0.2 }}
                    >
                      {formData.gstin || "N/A"}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="caption"
                      sx={{ color: "#64748b", fontWeight: 600 }}
                    >
                      PAN Number
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 700, color: "#0f172a", mt: 0.2 }}
                    >
                      {formData.pan || "N/A"}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* 3. Bank Details Card */}
            <Grid item xs={12} sm={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: "12px",
                  border: "1px solid #e2e8f0",
                  bgcolor: "#fff",
                  height: "100%",
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 700,
                    mb: 2,
                    color: "#0f172a",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <BankIcon sx={{ color: "#2563eb", fontSize: 20 }} />
                  Bank Account Details
                </Typography>

                <Grid container spacing={1.5}>
                  <Grid item xs={6}>
                    <Typography
                      variant="caption"
                      sx={{ color: "#64748b", fontWeight: 600 }}
                    >
                      Bank Name
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 700, color: "#0f172a" }}
                    >
                      {formData.bankName || "N/A"}
                    </Typography>
                  </Grid>

                  <Grid item xs={6}>
                    <Typography
                      variant="caption"
                      sx={{ color: "#64748b", fontWeight: 600 }}
                    >
                      Account Number
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 700, color: "#0f172a" }}
                    >
                      {formData.accountNumber || "N/A"}
                    </Typography>
                  </Grid>

                  <Grid item xs={6}>
                    <Typography
                      variant="caption"
                      sx={{ color: "#64748b", fontWeight: 600 }}
                    >
                      IFSC Code
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 700, color: "#0f172a" }}
                    >
                      {formData.ifscCode || "N/A"}
                    </Typography>
                  </Grid>

                  <Grid item xs={6}>
                    <Typography
                      variant="caption"
                      sx={{ color: "#64748b", fontWeight: 600 }}
                    >
                      Branch
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 700, color: "#0f172a" }}
                    >
                      {formData.branchName || "N/A"}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </Grid>

        {/* Right Column: Logo Display Card */}
        <Grid item xs={12} md={4} lg={3.5}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: "12px",
              border: "1px solid #e2e8f0",
              bgcolor: "#fff",
              height: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 700, color: "#0f172a", mb: 0.5 }}
            >
              Application Brand Logo
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: "#64748b", display: "block", mb: 3 }}
            >
              Displayed across invoices, header, login screen, and reports.
            </Typography>

            <Box
              sx={{
                p: 2,
                borderRadius: "16px",
                bgcolor: "#f8fafc",
                border: "1px dashed #cbd5e1",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "100%",
                maxWidth: 220,
                minHeight: 120,
              }}
            >
              <Avatar
                src={logoPreview || formData.logoUrl}
                variant="rounded"
                sx={{
                  width: "100%",
                  height: 100,
                  objectFit: "contain",
                  bgcolor: "transparent",
                }}
              >
                <BusinessIcon sx={{ color: "#94a3b8", fontSize: 48 }} />
              </Avatar>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* EDIT PROFILE MODAL (POPUP) */}
      <Dialog
        open={openEditModal}
        onClose={() => setOpenEditModal(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: "16px", p: 1 },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            fontWeight: 800,
            color: "#0f172a",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <EditIcon sx={{ color: "#2563eb" }} />
            Edit Company Profile
          </Box>
          <IconButton onClick={() => setOpenEditModal(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ borderColor: "#f1f5f9" }}>
          {/* Logo Upload Section in Modal */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2.5,
              p: 2,
              mb: 3,
              borderRadius: "12px",
              bgcolor: "#f8fafc",
              border: "1px solid #e2e8f0",
            }}
          >
            <Avatar
              src={logoPreview || formData.logoUrl}
              variant="rounded"
              sx={{
                width: 100,
                height: 60,
                objectFit: "contain",
                bgcolor: "#fff",
                border: "1px solid #cbd5e1",
                p: 0.5,
              }}
            >
              <BusinessIcon sx={{ color: "#94a3b8", fontSize: 32 }} />
            </Avatar>

            <Box>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 700, color: "#0f172a" }}
              >
                Update Main Logo
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: "#64748b", display: "block", mb: 1 }}
              >
                Supported formats: PNG, JPG, JPEG (Max size: 2MB)
              </Typography>
              <Button
                variant="outlined"
                component="label"
                startIcon={<CloudUploadIcon />}
                size="small"
                sx={{
                  textTransform: "none",
                  borderRadius: "8px",
                  fontWeight: 600,
                }}
              >
                Choose File
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={handleLogoChange}
                />
              </Button>
            </Box>
          </Box>

          {/* Form Fields */}
          {/* 1. Basic Info */}
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 700,
              mb: 1.5,
              color: "#0f172a",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <BusinessIcon sx={{ color: "#2563eb", fontSize: 18 }} />
            Company Information
          </Typography>

          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Company Name"
                name="companyName"
                value={formData.companyName}
                onChange={handleInputChange}
                size="small"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Tagline / Motto"
                name="tagline"
                value={formData.tagline}
                onChange={handleInputChange}
                size="small"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Official Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                size="small"
                error={!!errors.email}
                helperText={errors.email}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Contact Phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                size="small"
                placeholder="9876543210"
                error={!!errors.phone}
                helperText={errors.phone}
                inputProps={{
                  maxLength: 10,
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Website URL"
                name="website"
                value={formData.website}
                onChange={handleInputChange}
                size="small"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Business Address"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                size="small"
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 2.5 }} />

          {/* 2. Tax & Legal Info */}
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 700,
              mb: 1.5,
              color: "#0f172a",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <TaxIcon sx={{ color: "#2563eb", fontSize: 18 }} />
            Tax & Identity Registration
          </Typography>

          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="GSTIN Number"
                name="gstin"
                value={formData.gstin}
                onChange={handleInputChange}
                placeholder="22ABCDE1234F1Z5"
                size="small"
                error={!!errors.gstin}
                helperText={errors.gstin}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="PAN Number"
                name="pan"
                value={formData.pan}
                onChange={handleInputChange}
                placeholder="ABCDE1234F"
                size="small"
                error={!!errors.pan}
                helperText={errors.pan}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 2.5 }} />

          {/* 3. Bank Details */}
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 700,
              mb: 1.5,
              color: "#0f172a",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <BankIcon sx={{ color: "#2563eb", fontSize: 18 }} />
            Bank Account Details
          </Typography>

          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Bank Name"
                name="bankName"
                value={formData.bankName}
                onChange={handleInputChange}
                size="small"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Account Number"
                name="accountNumber"
                value={formData.accountNumber}
                onChange={handleInputChange}
                size="small"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="IFSC Code"
                name="ifscCode"
                value={formData.ifscCode}
                onChange={handleInputChange}
                size="small"
                error={!!errors.ifscCode}
                helperText={errors.ifscCode}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Branch Name"
                name="branchName"
                value={formData.branchName}
                onChange={handleInputChange}
                size="small"
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setOpenEditModal(false)}
            sx={{ textTransform: "none", color: "#64748b", fontWeight: 600 }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSaveProfile}
            disabled={loading}
            sx={{
              bgcolor: "#2563eb",
              color: "#fff",
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 700,
              px: 3,
              boxShadow: "0 4px 12px rgba(37, 99, 235, 0.25)",
              "&:hover": {
                bgcolor: "#1d4ed8",
              },
            }}
          >
            {loading ? (
              <CircularProgress size={20} sx={{ color: "#fff" }} />
            ) : (
              "Save Changes"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <ToastContainer position="top-right" autoClose={3000} />
    </Box>
  );
};

export default CompanyProfileManagement;
